import tw, { styled } from 'twin.macro'
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import CallToAction from '../components/CallToAction'
import PricingCalculator from '../components/PricingCalculator'
import SubscribeTool from '../components/SubscribeTool'
import TryTool from '.././apps/try'
import RelatedItems from '../components/RelatedItems'
import { ContentWrapper, ContentWrapperCenter, ContentWrapperSmall, HeadingContainer, PageHeading, PageSubTitle } from '../components/Typography'
import Content, { HTMLContent } from '../components/Content'
import Faq from '../components/Faq';
import Icons from '../icons'

const Container = styled('div')`
  ${tw`max-w-4xl mx-auto`}
  ${props => props.grid ? tw`sm:grid grid-cols-2 gap-4 sm:py-16` : ''}

  .staff-thumbnail {
    ${tw`rounded-full`}
  }
`

const defaultCTA = 'Upload a spreadsheet now. No credit card required.';

class LandingPageTemplate extends Component {
  renderContent() {
    const BodyContent = this.props.contentComponent || Content;

    return <BodyContent content={this.props.content} />
  }

  renderLogos(logos) {
    return (
      <div tw="bg-white">
        <div tw="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div tw="grid grid-cols-2 gap-8 md:grid-cols-6 lg:grid-cols-5">
            {logos && logos.map(logo => (
              <div key={logo.url} tw="col-span-1 flex justify-center md:col-span-2 lg:col-span-1">
                <img tw="h-12" src={logo.url} alt={logo.title} />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  renderFeatures(featureList) {
    return featureList.compact ? (
      <div key={featureList.title || ''} tw="bg-gray-50 px-8 sm:px-0 py-8 md:py-16">
        <Container>
          {featureList.title && <h2 tw="text-2xl md:text-3xl font-bold text-center mb-12">{featureList.title}</h2>}

          <div tw="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {featureList && featureList.items && featureList.items.map((content, index) => {
              const Icon = Icons[content.icon] || Icons.CheckCircle;
              const ContainerElement = content.url ? 'a' : 'div';

              return (
                <ContainerElement key={index} href={content.url || null} target={content.url ? '_blank' : null} rel={content.url ? 'noreferrer' : null} tw="flex items-center">
                  <Icon tw="w-6 h-6 flex-none text-teal-500 mr-2" />
                  <h3 tw="font-bold text-base md:text-lg">{content.title}</h3>
                </ContainerElement>
              )
            })}
          </div>
        </Container>
      </div>
    ) : (
      <div key={featureList.title || ''} tw="bg-gray-50 px-8 sm:px-0 py-8 md:py-16">
        <Container>
          {featureList.title && <h2 tw="text-2xl md:text-3xl font-bold text-center mb-12">{featureList.title}</h2>}

          <div tw="grid sm:grid-cols-3 gap-8">
            {featureList && featureList.items && featureList.items.map((content, index) => {
              const Icon = Icons[content.icon] || Icons.CheckCircle;
              const ContainerElement = content.url ? 'a' : 'div';

              return (
                <ContainerElement key={index} href={content.url || null} target={content.url ? '_blank' : null} rel={content.url ? 'noreferrer' : null} tw="flex flex-col items-center text-center">
                  <div tw="w-32 h-32 rounded-full bg-teal-500 shadow-2xl mb-4 flex items-center">
                    <Icon tw="w-16 h-16 text-white mx-auto" />
                  </div>
                  <h3 tw="font-bold text-lg md:text-2xl mb-4">{content.title}</h3>
                  {content.text && content.text.length > 0 && <HTMLContent content={content.text} css={tw`mx-auto text-base md:text-lg text-gray-500 leading-relaxed md:leading-relaxed`} />}
                </ContainerElement>
              )
            })}
          </div>
        </Container>
      </div>
    );
  }

  renderTestimonials(testimonials) {
    if (testimonials.length === 1) {
      return this.renderFeaturedTestimonial(testimonials[0]);
    }

    return (
      <div tw="max-w-7xl mx-auto my-8 md:grid md:grid-cols-2 md:gap-4 md:px-6 lg:px-8">
        {testimonials && testimonials.map(testimonial => (
          <div key={testimonial.name} tw="py-12 px-4 sm:px-6 md:flex md:flex-col md:py-16 bg-gray-50">
            <div tw="md:flex-shrink-0">
              {testimonial.logoUrls && testimonial.logoUrls.map(logoUrl => <img key={logoUrl} tw="h-12" src={logoUrl} alt={testimonial.name} />)}
            </div>
            <blockquote tw="mt-6 md:flex-grow md:flex md:flex-col">
              <div tw="relative text-lg font-medium md:flex-grow">
                <svg tw="absolute top-0 left-0 transform -translate-x-3 -translate-y-2 h-8 w-8 text-gray-300" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p tw="relative">
                  {testimonial.quote || testimonial.text}
                </p>
              </div>
              <footer tw="mt-8">
                <div tw="text-base font-medium" dangerouslySetInnerHTML={{__html: testimonial.author}}></div>
                <div tw="text-base font-medium text-gray-600" dangerouslySetInnerHTML={{__html: testimonial.name}}></div>
              </footer>
            </blockquote>
          </div>
        ))}
      </div>
    );
  }

  renderCitations(citations) {
    return (
      <div tw="max-w-7xl mx-auto my-8 md:grid md:grid-cols-2 md:gap-4 md:px-6 lg:px-8">
        {citations && citations.map(citation => (
          <div key={citation.name} tw="py-12 px-4 sm:px-6 md:flex justify-evenly md:py-16 bg-gray-50">
            <div tw="flex-1 flex items-center">
              <div tw="bg-white p-2 rounded-lg md:mx-8 shadow-lg">
                {citation.logoUrls && citation.logoUrls.map(logoUrl => <img key={logoUrl} src={logoUrl} alt={citation.name} />)}
              </div>
            </div>
            <div tw="flex-1 flex items-center">
              <div>
                <h4 tw="text-lg font-bold">
                  {citation.url ? <a href={citation.url} target="_blank" rel="noreferrer">{citation.description}</a> : citation.description}
                </h4>
                <footer tw="mt-4">
                  <div tw="text-base font-medium" dangerouslySetInnerHTML={{__html: citation.author}}></div>
                  <div tw="text-base font-medium text-gray-600" dangerouslySetInnerHTML={{__html: citation.name}}></div>
                </footer>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderFeaturedTestimonial(testimonial) {
    let text = testimonial.text;
    let quote = testimonial.quote;

    if (!quote && text) {
      quote = text.replace(/(<([^>]+)>)/gi, '');
      text = null;
    }

    return (
      <section tw="py-12 bg-white overflow-hidden md:py-20 lg:py-24">
        <div tw="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <svg tw="absolute top-full right-full transform translate-x-1/3 -translate-y-1/4 lg:translate-x-1/2 xl:-translate-y-1/2" width="404" height="404" fill="none" viewBox="0 0 404 404" role="img" aria-label="Textured Background">
            <defs>
              <pattern id="ad119f34-7694-4c31-947f-5c9d249b21f3" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" tw="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#ad119f34-7694-4c31-947f-5c9d249b21f3)" />
          </svg>

          <div tw="relative">
            {testimonial && testimonial.logoUrls && testimonial.logoUrls.map(logoUrl => <img key={logoUrl} src={logoUrl} tw="mx-auto h-16" alt={testimonial.name} />)}

            {testimonial.text && <ContentWrapperSmall tw="max-w-3xl mx-auto">
              <HTMLContent content={text} />
            </ContentWrapperSmall>}

            <blockquote tw="mt-10">
              <div tw="max-w-3xl mx-auto text-center text-2xl leading-9 font-medium text-gray-900">
                &ldquo;{quote}&rdquo;
              </div>
              <footer tw="mt-8">
                <div tw="md:flex md:items-center md:justify-center">
                  <div tw="mt-3 text-center md:mt-0 md:ml-4 md:flex md:items-center">
                    <div tw="text-base font-medium text-gray-900" dangerouslySetInnerHTML={{__html: testimonial.author}}></div>

                    <svg tw="hidden md:block mx-1 h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 0h3L9 20H6l5-20z" />
                    </svg>

                    <div tw="text-base font-medium text-gray-500" dangerouslySetInnerHTML={{__html: testimonial.name}}></div>
                  </div>
                </div>
              </footer>
            </blockquote>
          </div>
        </div>
      </section>
    )
  }

  renderSections(hasImage) {
    if (this.props.sections && this.props.sections.length > 0) {
      const SelectedContentWrapper = hasImage ? ContentWrapper : ContentWrapperCenter;

      return (
        <div>
          {this.props.sections.map((item, index) => (
            <Container key={index}>
              <SelectedContentWrapper tw="p-6">
                <HTMLContent content={item.text} />
              </SelectedContentWrapper>
            </Container>
          ))}
        </div>
      );
    }
  }

  renderContentWithoutImage() {
    return (
      <Container grid={false}>
        <ContentWrapperCenter>
          {this.renderContent()}
        </ContentWrapperCenter>
      </Container>
    );
  }

  renderContentWithImage(pageImage, title) {
    return (
      <div tw="relative isolate overflow-hidden bg-white lg:overflow-visible">
        <div tw="mx-auto grid max-w-2xl grid-cols-1 gap-y-16 gap-x-8 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-y-10">
          <div tw="-ml-12 p-12 lg:sticky lg:top-4 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:overflow-hidden">
            <img tw="w-[24rem] max-w-none sm:w-[32rem]" src={pageImage} title={title} alt={title} />
          </div>
          <div tw="lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8">
            <div tw="lg:pr-4">
              <div tw="max-w-xl text-base leading-7 text-gray-700 lg:max-w-lg">
                <ContentWrapper>
                {this.renderContent()}
                </ContentWrapper>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  componentDidMount() {
    this.initTallyForms();
  }

  initTallyForms() {
    // Check if Tally is already defined
    if (typeof window.Tally !== 'undefined') {
      window.Tally.loadEmbeds();
    } else {
      // Check if script is already loaded
      const tallyScriptUrl = "https://tally.so/widgets/embed.js";
      if (!document.querySelector(`script[src="${tallyScriptUrl}"]`)) {
        const script = document.createElement("script");
        script.src = tallyScriptUrl;
        script.onload = () => {
          if (typeof window.Tally !== 'undefined') {
            window.Tally.loadEmbeds();
          } else {
            // Fallback - directly set src on iframes if Tally object isn't available
            document.querySelectorAll("iframe[data-tally-src]:not([src])").forEach((iframe) => {
              iframe.src = iframe.dataset.tallySrc;
            });
          }
        };
        script.onerror = () => {
          // Fallback if script fails to load
          document.querySelectorAll("iframe[data-tally-src]:not([src])").forEach((iframe) => {
            iframe.src = iframe.dataset.tallySrc;
          });
        };
        document.body.appendChild(script);
      }
    }
  }

  render() {
    const {
      title,
      description,
      tools,
      logos,
      image,
      testimonials,
      citations,
      related,
      features,
      faq,
      cta,
      ctaSubtitle,
      ctaButtonTitle,
      ctaButtonLink
    } = this.props;

    const clonedTestimonials = testimonials ? testimonials.slice(0) : [];
    const clonedFeatures = features ? features.slice(0) : [];

    const featuredTestimonials = clonedTestimonials.filter(testimonial => testimonial.featured);
    const remainingTestimonials = clonedTestimonials.filter(testimonial => !testimonial.featured);

      const firstTestimonial = remainingTestimonials.length > 0
        ? remainingTestimonials.shift()
        : null;

    const firstFeatureList = clonedFeatures.length > 0 && (!clonedFeatures[0].compact || clonedFeatures[0].compact === false)
      ? clonedFeatures.shift()
      : null;

    let pageImage = image;
    if (pageImage && pageImage.length <= 0) {
      pageImage = null;
    }

    return (
      <div>
        <HeadingContainer>
          <PageHeading>{title}</PageHeading>
          <PageSubTitle>{description}</PageSubTitle>
        </HeadingContainer>

        {featuredTestimonials.length > 0 && this.renderTestimonials(featuredTestimonials)}

        {pageImage ? this.renderContentWithImage(pageImage, title) : this.renderContentWithoutImage()}

        {tools && tools.try && tools.try.enabled && <TryTool {...tools.try} />}
        {tools && tools.pricing_calculator && tools.pricing_calculator.enabled && <PricingCalculator {...tools.pricing_calculator} />}
        {tools && tools.subscribe && tools.subscribe.enabled && <SubscribeTool {...tools.subscribe} />}

        {this.renderSections(pageImage != null)}

        {firstFeatureList && this.renderFeatures(firstFeatureList)}
        {firstTestimonial && this.renderFeaturedTestimonial(firstTestimonial)}
        {logos && logos.length > 0 && this.renderLogos(logos)}
        {clonedFeatures.length > 0 && clonedFeatures.map(feature => this.renderFeatures(feature))}
        {remainingTestimonials.length > 0 && this.renderTestimonials(remainingTestimonials)}
        {citations && citations.length > 0 && this.renderCitations(citations)}

        <CallToAction title={cta || defaultCTA} subtitle={ctaSubtitle || null} buttonTitle={ctaButtonTitle || null} buttonLink={ctaButtonLink || null} />
        {faq && faq.length > 0 && <Faq items={faq} />}
        <RelatedItems items={related} />
      </div>
    )
  }
}

LandingPageTemplate.defaultProps = {
  image: null
}

LandingPageTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
  tools: PropTypes.object,
  logos: PropTypes.array,
  image: PropTypes.string,
  features: PropTypes.array,
  faq: PropTypes.array,
  sections: PropTypes.array,
  testimonials: PropTypes.array,
  related: PropTypes.array,
  cta: PropTypes.string,
  ctaSubtitle: PropTypes.string,
  ctaButtonTitle: PropTypes.string,
  ctaButtonLink: PropTypes.string
}

export default LandingPageTemplate;